/**
 * "Advanced styles" (plan B3 option B, Stages 2-3): a table of additive representations, color,
 * transparency and cartoon type per group of atoms, for either 3D viewer. The logic lives in
 * advancedStylesModel.js; this file is only the UI.
 *
 *   AdvancedStylesDrawerButton  the "Advanced styles" entry in the atom-style tray
 *   AdvancedStylesPanelHost     always mounted (RightControlPanel); shows the panel when asked
 *   AdvancedStylesPanel         a non-modal panel: groups x representations, options, reset; the
 *                               3D view stays usable while it is open
 */
import {
    Fragment, useCallback, useEffect, useMemo, useState,
} from 'react';
import Paper from '@mui/material/Paper';
import Checkbox from '@mui/material/Checkbox';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableBody from '@mui/material/TableBody';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import Tooltip from '@mui/material/Tooltip';
import Popover from '@mui/material/Popover';
import CloseIcon from '@mui/icons-material/Close';
import TuneIcon from '@mui/icons-material/Tune';
import { App } from 'BMapsSrc/BMapsApp';
import { UserActions } from 'BMapsCmds';
import { EventBroker } from 'BMapsSrc/eventbroker';
import { StyleManager } from 'BMapsSrc/style_manager';
import { supportedRepSetReps, perGroupTransparentReps } from 'BMapsSrc/MainCanvas';
import { VisualizerSwitchedEvent } from 'BMapsSrc/VisualizerInterface';
import DrawerButton from '../RightControlPanel/base/DrawerButton';
import useEventBrokerSubscription from '../common/helpers/useEventBrokerSubscription';
import {
    AdvancedStylesModel, AdvancedStyleReps, AdvancedStyleColors, AdvancedStyleTransparencies,
    AdvancedStyleCartoonTypes, AdvancedStylePaletteColors,
} from './advancedStylesModel';

/** EventBroker event: open (payload true / none) or close (false) the panel. */
export const ShowAdvancedStylesEvent = 'showAdvancedStyles';
const RefreshMs = 2000;

/**
 * The model with the app's own objects, looked up when used: the panel host is mounted with the
 * page, before App.Workspace exists (capturing it then left the model with `undefined`).
 */
export function makeAppModel() {
    return new AdvancedStylesModel({
        workspace: {
            allAtomGroups: () => App.Workspace.allAtomGroups(),
            getVisibleCompounds: (active) => App.Workspace.getVisibleCompounds(active),
            getAllHotspots: () => App.Workspace.getAllHotspots(),
            getDefaultAtomGroupStyle: (group) => App.Workspace.getDefaultAtomGroupStyle(group),
        },
        userActions: UserActions,
        styleManager: StyleManager,
        supportedReps: () => {
            try {
                return supportedRepSetReps();
            } catch (ex) {
                return [];
            }
        },
        transparentReps: () => {
            try {
                return perGroupTransparentReps();
            } catch (ex) {
                return [];
            }
        },
    });
}

export function AdvancedStylesDrawerButton() {
    return (
        <DrawerButton
            id="advanced-styles"
            title="Representations and color per group of atoms"
            onClick={() => EventBroker.publish(ShowAdvancedStylesEvent, true)}
        >
            Advanced styles
        </DrawerButton>
    );
}

export function AdvancedStylesPanelHost({ model: modelIn=null }) {
    const [open, setOpen] = useState(false);
    const model = useMemo(() => modelIn || makeAppModel(), [modelIn]);
    const onShow = useCallback((_evt, show) => setOpen(show !== false), []);
    const onEscape = useCallback(() => setOpen(false), []);
    useEventBrokerSubscription(ShowAdvancedStylesEvent, onShow);
    useEventBrokerSubscription('escapeKey', onEscape);
    if (!open) return null;
    return <AdvancedStylesPanel model={model} onClose={() => setOpen(false)} />;
}

const checkState = (value) => ({ checked: value === 'all', indeterminate: value === 'some' });

/** A row's dropdown; `value` null = the row's groups differ ("Mixed"). */
function RowSelect({
    label, value, choices, onChange, minWidth='8em',
}) {
    return (
        <TextField
            select
            size="small"
            variant="standard"
            value={value ?? ''}
            // The label goes on the visible dropdown (the native input is hidden).
            // MUI points aria-labelledby at the dropdown itself, which would win.
            slotProps={{
                select: {
                    displayEmpty: true,
                    SelectDisplayProps: { 'aria-label': label, 'aria-labelledby': undefined },
                },
            }}
            sx={{ minWidth, '& .MuiSelect-select': { fontSize: '0.8rem', py: 0 } }}
            onChange={(e) => onChange(e.target.value)}
        >
            <MenuItem value="" disabled>Mixed</MenuItem>
            {choices.map((c) => (
                <MenuItem key={c.label} value={c.value}>{c.label}</MenuItem>
            ))}
        </TextField>
    );
}

const PopoverGrid = {
    padding: '0.5em 1em', display: 'grid', gridTemplateColumns: 'auto auto', gap: '0.3em 1em',
};

const TransparencyReps = [
    ['sticks', 'Sticks'], ['spheres', 'Spheres'], ['cartoon', 'Cartoon'], ['surface', 'Surface'],
];

/** The row's Transparency select (every rep) and a popover with one per rep. */
function TransparencyCell({ row, model, act }) {
    const [anchor, setAnchor] = useState(null);
    const byRep = row.transparencyByRep || {};
    return (
        <div style={{ display: 'flex', alignItems: 'center' }}>
            <RowSelect
                label={`Transparency for ${row.label}`}
                value={row.transparency}
                choices={AdvancedStyleTransparencies}
                minWidth="4em"
                onChange={(value) => act(() => model.setTransparency(row.id, value))}
            />
            <IconButton
                size="small"
                sx={{ p: 0.25 }}
                aria-label={`Transparency per representation for ${row.label}`}
                onClick={(e) => setAnchor(e.currentTarget)}
            >
                <TuneIcon sx={{ fontSize: '1rem' }} />
            </IconButton>
            <Popover
                open={!!anchor}
                anchorEl={anchor}
                onClose={() => setAnchor(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            >
                <div style={PopoverGrid}>
                    {TransparencyReps.map(([rep, repLabel]) => (
                        <Fragment key={rep}>
                            <Typography variant="body2">{repLabel}</Typography>
                            <RowSelect
                                label={`${repLabel} transparency for ${row.label}`}
                                value={byRep[rep]}
                                choices={AdvancedStyleTransparencies}
                                minWidth="4em"
                                onChange={(value) => act(
                                    () => model.setTransparency(row.id, value, rep),
                                )}
                            />
                        </Fragment>
                    ))}
                </div>
            </Popover>
        </div>
    );
}

/** A row's color, transparency and cartoon type (the whole group's, so not on a chain's parts). */
function RowOptions({ row, model, act }) {
    return (
        <>
            <TableCell>
                <RowSelect
                    label={`Color for ${row.label}`}
                    value={row.color}
                    choices={AdvancedStyleColors}
                    onChange={(value) => act(() => model.setColor(row.id, value))}
                />
            </TableCell>
            <TableCell>
                <TransparencyCell row={row} model={model} act={act} />
            </TableCell>
            <TableCell>
                <RowSelect
                    label={`Cartoon type for ${row.label}`}
                    value={row.cartoonType}
                    choices={AdvancedStyleCartoonTypes}
                    minWidth="6em"
                    onChange={(value) => act(() => model.setCartoonType(row.id, value))}
                />
            </TableCell>
        </>
    );
}

/** Hotspots: a color and Reset (reps, labels and rep-set options do not apply to clouds). */
function ColorOnlyRow({ row, model, act }) {
    return (
        <TableRow data-row={row.label} data-kind={row.kind}>
            <TableCell sx={{ whiteSpace: 'nowrap' }}>{row.label}</TableCell>
            <TableCell colSpan={AdvancedStyleReps.length + 1} />
            <TableCell>
                <RowSelect
                    label={`Color for ${row.label}`}
                    value={row.color}
                    choices={AdvancedStylePaletteColors}
                    onChange={(value) => act(() => model.setColor(row.id, value))}
                />
            </TableCell>
            <TableCell colSpan={2} />
            <TableCell>
                <Button
                    size="small"
                    aria-label={`Reset ${row.label}`}
                    sx={{ minWidth: 0, py: 0 }}
                    onClick={() => act(() => model.reset(row.id))}
                >
                    Reset
                </Button>
            </TableCell>
        </TableRow>
    );
}

function StyleRow({
    row, available, model, act,
}) {
    if (row.colorOnly) return <ColorOnlyRow row={row} model={model} act={act} />;
    return (
        <TableRow data-row={row.label} data-kind={row.kind}>
            <TableCell
                sx={{
                    whiteSpace: 'nowrap',
                    fontWeight: row.kind === 'all' ? 'bold' : undefined,
                    // a chain's main chain / side chains: indented under it
                    ...(row.scope ? { pl: '1.5em !important', color: 'text.secondary' } : {}),
                }}
            >
                {row.part || row.label}
            </TableCell>
            {AdvancedStyleReps.map((rep) => {
                const box = (
                    <Checkbox
                        size="small"
                        sx={{ p: 0.25 }}
                        disabled={!available[rep.id]}
                        {...checkState(row.reps[rep.id])}
                        slotProps={{ input: { 'aria-label': `${rep.label} for ${row.label}` } }}
                        onChange={() => act(() => model.toggle(row.id, rep.id))}
                    />
                );
                return (
                    <TableCell key={rep.id} align="center">
                        {available[rep.id] ? box : (
                            <Tooltip title="Not drawn by this 3D viewer"><span>{box}</span></Tooltip>
                        )}
                    </TableCell>
                );
            })}
            <TableCell align="center">
                {row.labels && (
                    <Checkbox
                        size="small"
                        sx={{ p: 0.25 }}
                        {...checkState(row.labels)}
                        slotProps={{ input: { 'aria-label': `Labels for ${row.label}` } }}
                        onChange={() => act(() => model.toggleLabels(row.id))}
                    />
                )}
            </TableCell>
            {row.scope ? <TableCell colSpan={3} /> : (
                <RowOptions row={row} model={model} act={act} />
            )}
            <TableCell>
                <Button
                    size="small"
                    aria-label={`Reset ${row.label}`}
                    sx={{ minWidth: 0, py: 0 }}
                    onClick={() => act(() => model.reset(row.id))}
                >
                    Reset
                </Button>
            </TableCell>
        </TableRow>
    );
}

const joinNames = (names) => (names.length > 1
    ? `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}` : names[0] || '');

/** What the Transparency column does with this viewer. */
export function transparencyNote(transparentReps) {
    const perGroup = ['sticks', 'spheres', 'cartoon', 'surface'].filter((r) => transparentReps.includes(r));
    if (perGroup.length === 0) return 'This 3D viewer draws no transparency.';
    const shared = ['sticks', 'spheres'].filter((r) => !perGroup.includes(r));
    return `Transparency applies to ${joinNames(perGroup)}${shared.length > 0
        ? `; this 3D viewer makes ${joinNames(shared)} transparent only when all groups share it` : ''}.`;
}

export function AdvancedStylesPanel({ model, onClose }) {
    const [table, setTable] = useState(() => model.read());
    const [message, setMessage] = useState(null);

    const refresh = useCallback(() => {
        try {
            setTable(model.read());
        } catch (ex) {
            setMessage(`Could not read the current styles: ${(ex && ex.message) || ex}`);
        }
    }, [model]);

    useEffect(() => {
        const timer = setInterval(refresh, RefreshMs); // BMaps may restyle meanwhile
        return () => clearInterval(timer);
    }, [refresh]);
    // another viewer draws other representations
    useEventBrokerSubscription(VisualizerSwitchedEvent, refresh);

    const act = (fn) => {
        try {
            fn();
            setMessage(null);
        } catch (ex) {
            setMessage(String((ex && ex.message) || ex));
        }
        refresh();
    };

    const { rows, available, transparentReps = [] } = table;
    const missing = AdvancedStyleReps.filter((rep) => !available[rep.id])
        .map((rep) => rep.label.toLowerCase());
    return (
        <Paper
            elevation={8}
            data-testid="advanced-styles-panel"
            role="dialog"
            aria-label="Advanced styles"
            sx={{
                // bottom right, above the color swatches: the middle of the view stays visible
                position: 'fixed',
                right: '7.5em',
                bottom: '4.5em',
                zIndex: 7, // above the open drawers (6)
                maxWidth: 'calc(100vw - 10em)',
                maxHeight: 'calc(100vh - 10em)',
                overflow: 'auto',
                p: 1.5,
            }}
        >
            <div style={{ display: 'flex', alignItems: 'center', gap: '1em' }}>
                <Typography variant="h6" sx={{ fontSize: '1rem', flex: 1 }}>Advanced styles</Typography>
                <IconButton size="small" aria-label="Close" onClick={onClose}><CloseIcon fontSize="small" /></IconButton>
            </div>
            {rows.length === 0 ? (
                <Typography variant="body2" sx={{ my: 1 }}>Nothing is loaded yet.</Typography>
            ) : (
                <Table size="small" sx={{ '& td, & th': { px: 0.5, py: 0.25 } }}>
                    <TableHead>
                        <TableRow>
                            <TableCell>Group</TableCell>
                            {AdvancedStyleReps.map((rep) => <TableCell key={rep.id} align="center">{rep.label}</TableCell>)}
                            <TableCell align="center">Labels</TableCell>
                            <TableCell>Color</TableCell>
                            <TableCell>Transparency</TableCell>
                            <TableCell>Cartoon</TableCell>
                            <TableCell />
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rows.map((row) => (
                            <StyleRow
                                key={row.id}
                                row={row}
                                available={available}
                                model={model}
                                act={act}
                            />
                        ))}
                    </TableBody>
                </Table>
            )}
            {message && <Typography variant="body2" color="error" sx={{ mt: 1 }}>{message}</Typography>}
            <Typography variant="caption" color="textSecondary" component="p" sx={{ mt: 1, maxWidth: '40em' }}>
                These are BMaps styles: they stay when the scene is redrawn or the 3D viewer
                is switched, and are saved with sessions. Reset gives a group BMaps&apos; own
                styles back.
                {` ${transparencyNote(transparentReps)}`}
                {' Putty scales the tube by B-factor in the viewer that includes PyMOL\u2122 (a plain'
                    + ' tube when the structure has no B-factors, and always in 3Dmol).'}
                {missing.length > 0 && ` This 3D viewer does not draw ${missing.join(', ')} - the viewer`
                    + ' that includes PyMOL\u2122 does.'}
                {' PyMOL is a trademark of Schrodinger, LLC.'}
            </Typography>
        </Paper>
    );
}
